export type EntryType = 
  | "expense" 
  | "givenCheck" 
  | "receivedCheck" 
  | "laborCost" 
  | "delivery" 
  | "sitePhoto"
  | "sale";

export type PersonCategory = 
  | "laborer"           // Display: Yüklenici - Usta
  | "supplier"          // Display: Malzeme Tedarikçisi
  | "subcontractor"     // Display: Alt Yüklenici
  | "employer"          // Display: İşveren - Müteahhit
  | "partner"           // Display: Ortak
  | "engineering"       // Display: Mühendislik Hizmetleri
  | "other";            // Display: Diğer

// Shared category options mapping DB values (PersonCategory) to TR labels
export const CATEGORY_OPTIONS: { value: PersonCategory; label: string }[] = [
  { value: "laborer", label: "Yüklenici - Usta" },
  { value: "supplier", label: "Malzeme Tedarikçisi" },
  { value: "subcontractor", label: "Alt Yüklenici" },
  { value: "employer", label: "İşveren - Müteahhit" },
  { value: "partner", label: "Ortak" },
  { value: "engineering", label: "Mühendislik Hizmetleri" },
  { value: "other", label: "Diğer" },
];

export interface Unit {
  type: 'flat' | 'shop';
  number: number;
  area: number;
}

export interface Person {
  id: string;
  name: string;
  category: PersonCategory;
  specialty?: string;
  avatar?: string;
  projectId?: string;
  totalAgreed?: number;
  totalPaid?: number;
  linked?: boolean;
}

export type ProjectCosts = {
  try: number;
  usd: number;
  eur: number;
};

export type DetailedProjectCosts = {
  official: {
    TRY: number;
    USD: number;
    EUR: number;
  };
  unofficial: {
    TRY: number;
    USD: number;
    EUR: number;
  };
  total: {
    TRY: number;
    USD: number;
    EUR: number;
  };
};

export interface Project {
  id: string;
  name: string;
  status: 'active' | 'completed' | 'on-track' | 'delayed';
  progress: number;
  costs: {
    try: number;
    usd: number;
    eur: number;
  };
  details?: {
    parcel: string;
    lot: string;
    flats: number;
    shops: number;
    area: number;
    costPerSquareMeter: number;
    units?: Array<{
      type: 'flat' | 'shop';
      number: number;
      area: number;
    }>;
  };
  upcomingPayments?: Array<{
    amount: number;
    dueDate: string;
    dueInDays: number;
  }>;
  people?: Array<{
    id: string;
    name: string;
    role: string;
  }>;
  detailedCosts?: DetailedProjectCosts;
}

export interface Entry {
  id: string;
  type: EntryType;
  projectId: string;
  date: string;
  description: string;
  
  // Updated currency and amount fields with official/unofficial distinction
  amount_try_official?: number;
  amount_try_unofficial?: number;
  amount_usd_official?: number;
  amount_usd_unofficial?: number;
  amount_eur_official?: number;
  amount_eur_unofficial?: number;
  amount_gbp_official?: number;
  amount_gbp_unofficial?: number;
  amount_jpy_official?: number;
  amount_jpy_unofficial?: number;
  amount_rub_official?: number;
  amount_rub_unofficial?: number;
  amount_dinar_official?: number;
  amount_dinar_unofficial?: number;
  amount_sar_official?: number;
  amount_sar_unofficial?: number;
  amount_chf_official?: number;
  amount_chf_unofficial?: number;
  amount_xau_official?: number;
  amount_xau_unofficial?: number;
  amount_btc_official?: number;
  amount_btc_unofficial?: number;
  
  original_currency: 'TRY' | 'USD' | 'EUR' | 'GBP' | 'JPY' | 'RUB' | 'DINAR' | 'SAR' | 'CHF' | 'XAU' | 'BTC';
  original_amount?: number;
  is_official: boolean;
  hasInvoice: boolean;
  
  personId?: string;
  isMiscellaneous?: boolean; // Flag for "Muhtelif"
  
  // Exchange rates used for conversion (for transparency)
  usd_rate?: number;
  eur_rate?: number;
  
  invoiceNumber?: string;
  dueDate?: string;
}

export interface Material {
  id: string;
  name: string;
  brand: string;
  quantity: number;
  unit: string;
  area: string;
  projectId: string;
  lastUpdated: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  company: string;
  avatar: string;
  role: string;
  plan: string;
  trialDaysRemaining: number;
  is_dev_admin?: boolean;
  expo_push_token?: string;
}

export interface SubscriptionPlan {
  type: string;
  price: number;
  projectLimit: number;
  features: string[];
}

export interface ConstructionStage {
  id: string;
  name: string;
  completed: boolean;
  phase?: string;
  phaseColor?: string;
  completedDate?: Date;
  note?: string;
  photoUrl?: string;
  orderIndex?: number;
}

export type ReportType = "detailed" | "general";

export interface ReportProject {
  id: string;
  name: string;
}

export interface Report {
  id: string;
  type: ReportType;
  projectId: string;
  startDate: string;
  endDate: string;
  includePhotos: boolean;
  includeSignatures: boolean;
}

export interface Transaction {
  id: string;
  personId: string;
  projectId: string;
  projectName?: string;
  description: string;
  amount: number;
  date: Date;
  transactionType: 'laborCost' | 'payment' | 'check';
  paymentType?: string;
  metricDetails?: string;
  checkNumber?: string;
  dueInDays?: number;
  status?: string;
  
  // Legacy currency fields (for backward compatibility)
  usdAmount?: number;
  euroAmount?: number;
  
  // New currency fields with official/unofficial distinction
  amount_try_official?: number;
  amount_try_unofficial?: number;
  amount_usd_official?: number;
  amount_usd_unofficial?: number;
  amount_eur_official?: number;
  amount_eur_unofficial?: number;
  amount_gbp_official?: number;
  amount_gbp_unofficial?: number;
  amount_jpy_official?: number;
  amount_jpy_unofficial?: number;
  amount_rub_official?: number;
  amount_rub_unofficial?: number;
  amount_kwd_official?: number;
  amount_kwd_unofficial?: number;
  amount_sar_official?: number;
  amount_sar_unofficial?: number;
  amount_chf_official?: number;
  amount_chf_unofficial?: number;
  amount_xau_official?: number;
  amount_xau_unofficial?: number;
  amount_btc_official?: number;
  amount_btc_unofficial?: number;
  
  original_currency?: string;
  original_amount?: number;
  is_official?: boolean;
  
  image_url?: string | null;
  checkType?: 'givenCheck' | 'receivedCheck';
  is_shadow_entry?: boolean;

  // Payment-method model
  payment_method?: 'cash_card' | 'check' | 'advance' | 'open_account' | null;
  advance_sub_method?: 'cash_card' | 'check' | null;
  counts_toward_cost?: boolean | null;
  is_settled?: boolean | null;
  settled_method?: string | null;
  settles_entry_id?: string | null;
}

// Added UserProfile based on public.users table
export interface UserProfile {
  id: string; // UUID corresponds to auth.users.id
  email: string;
  name?: string | null;
  company?: string | null;
  avatar?: string | null;
  role?: string | null; // System role?
  plan?: string | null;
  trial_days_remaining?: number | null;
  created_at: string; // ISO timestamp
  updated_at: string; // ISO timestamp
  is_dev_admin?: boolean | null;
  notification_permission_asked?: boolean | null;
  permission_granted?: boolean | null;
  token_refresh_requested?: boolean | null;
  token_refresh_requested_at?: string | null; // ISO timestamp
  onesignal_user_id?: string | null;
  phone?: string | null;
  country?: string | null;
  city?: string | null;
  selected_role?: string | null; // User-selected role
}

// Added types for LocationSelector component
export interface OptionType {
  value: string;
  label: string;
}

export interface LocationValue {
  country: OptionType | null;
  city: OptionType | null;
}
